import { createReducer } from '@ngrx/store';
import {
  getAllAvailableExportSettingsFailure,
  getAllAvailableExportSettingsSuccess,
  getAvailableCadTypesFailure,
  getAvailableCadTypesSuccess,
  getAvailableDocumentTypesFailure,
  getAvailableDocumentTypesSuccess,
  getExportSettingsFailure,
  getExportSettingsSuccess,
  getZipSettingsForSessionFailure,
  getZipSettingsForSessionSuccess,
  postExportSettingsFailure,
  postExportSettingsSuccess,
  setAvailableLanguages,
  setCurrentStep,
  setDocumentSession,
  setInitialSession,
  setNeedToSaveSettings,
  setSelectedCadTypes,
  setSelectedDocumentTypes,
  setSelectedLanguages,
  setSelectedProducts,
  updateSavedConfiguration,
} from './mydoc.action';
import { initialMydocState } from './mydoc.state';
import { immerOn } from 'ngrx-immer/store';
import {
  getValue,
  isDefinedAndFilledArray,
} from '../shared/utility/verify.lib';
import { IExportSettingInput } from '../models/export/export-setting-input.model';
import { showExportSettingsTooltip } from '../models/export/export-settings-fe.model';
import { IExportStructure } from '../models/export/export-structure.model';
import { cloneDeep } from 'lodash';

const maxSettingEntries: number = 5;

export const mydocReducer = createReducer(
  initialMydocState,
  immerOn(setInitialSession, (state, action) => {
    state.session.id = action.id;
    state.session.orderNumber = action.orderNumber;
    state.session.language = action.language;
  }),
  immerOn(setDocumentSession, (state, action) => {
    state.session.documentSession = action.documentSession;
  }),
  immerOn(setSelectedDocumentTypes, (state, action) => {
    state.selectedDocumentTypes = action.documentTypes;
  }),
  immerOn(setSelectedCadTypes, (state, action) => {
    state.selectedCadTypes = action.cadTypes;
  }),
  immerOn(getAvailableDocumentTypesSuccess, (state, action) => {
    state.availableDocumentTypes = action.documentTypes;
  }),
  immerOn(getAvailableDocumentTypesFailure, (state) => {
    state.showErrorMessage = true;
  }),
  immerOn(getAvailableCadTypesSuccess, (state, action) => {
    state.availableCadTypes = action.cadTypes;
  }),
  immerOn(getAvailableCadTypesFailure, (state) => {
    state.showErrorMessage = true;
  }),
  immerOn(setSelectedProducts, (state, action) => {
    state.selectedProducts = action.products;
  }),
  immerOn(setAvailableLanguages, (state, action) => {
    state.language.available = action.languages;
  }),
  immerOn(setSelectedLanguages, (state, action) => {
    state.language.selected = action.languages;
  }),
  immerOn(getExportSettingsSuccess, (state, action) => {
    if (isDefinedAndFilledArray(() => action.settingsList)) {
      action.settingsList.forEach((setting: IExportSettingInput) => {
        if (getValue(() => setting.settingKey) === showExportSettingsTooltip) {
          let newTooltipToShow: boolean = true;
          if (setting?.value === 'false') {
            newTooltipToShow = false;
          }
          state.exportSettings.showExportSettingsTooltip = newTooltipToShow;
        }
      });
    }
  }),
  immerOn(getAllAvailableExportSettingsFailure, (state) => {
    state.showErrorMessage = true;
  }),
  immerOn(getExportSettingsFailure, (state) => {
    state.exportSettings.showExportSettingsTooltip = false;
    state.showErrorMessage = true;
  }),
  immerOn(getAllAvailableExportSettingsSuccess, (state, action) => {
    state.exportSettings.availableConfiguration = action.configuration;
  }),
  immerOn(getZipSettingsForSessionSuccess, (state, action) => {
    const missingFolderEntries: number =
      maxSettingEntries -
      getValue(() => action.configuration.folderSetting.length, 0);
    const missingFileEntries: number =
      maxSettingEntries -
      getValue(() => action.configuration.fileSetting.length, 0);

    const newFolderSettings: IExportStructure[] = cloneDeep(
      action.configuration.folderSetting
    ).concat(Array(missingFolderEntries).fill(null));
    const newFileSettings: IExportStructure[] = cloneDeep(
      action.configuration.fileSetting
    ).concat(Array(missingFileEntries).fill(null));

    state.exportSettings.defaultConfiguration = {
      ...action.configuration,
      folderSetting: newFolderSettings,
      fileSetting: newFileSettings,
    };
    state.exportSettings.savedConfiguration = {
      ...action.configuration,
      folderSetting: newFolderSettings,
      fileSetting: newFileSettings,
    };
  }),
  immerOn(getZipSettingsForSessionFailure, (state) => {
    state.showErrorMessage = true;
  }),
  immerOn(updateSavedConfiguration, (state, action) => {
    state.exportSettings.savedConfiguration = action.configuration;
  }),
  immerOn(postExportSettingsSuccess, (state) => {
    state.exportSettings.showExportSettingsTooltip = false;
  }),
  immerOn(postExportSettingsFailure, (state) => {
    state.showErrorMessage = true;
  }),
  immerOn(setCurrentStep, (state, action) => {
    state.currentStep = action.currentStep;
  }),
  immerOn(setNeedToSaveSettings, (state, action) => {
    state.needToSaveSettings = action.saveSettings;
  })
);
